/**
 * @file        webgpu-benchmark.js
 * @author      Amir Sojoodi, amir@distributive.network
 *              Ryan Saweczko, ryansaweczko@distributive.network
 * @date        May 2024
 *
 * This code measures the total time execution of a matrix multiplication, which
 * includes the computation time and the transfer data back from device to host
 *
 * Used as a benchmark to compare how fast the GPU within each worker is, run at the
 * same time we record the capabilities of a sandbox
 */

exports.benchmark = `
const BLOCK_SIZE_X = 16;
const BLOCK_SIZE_Y = 16;

let device = null;
let adapter = null;
let matrixWidth;

let firstMatrix;
let secondMatrix;

let gpuBufferFirstMatrix;
let gpuBufferSecondMatrix;

let arrayBufferFirstMatrix;
let arrayBufferSecondMatrix;
let resultMatrixBufferSize;
let resultMatrixBuffer;
let gpuReadBuffer;

async function initializeWebGPU()
{
  adapter = await navigator.gpu.requestAdapter();
  device = await adapter.requestDevice({ powerPreference : 'high-performance' });

  // requestDevice will never return null, but if a valid device request
  // can't be fulfilled for some reason it may resolve to a device which has
  // already been lost. Additionally, devices can be lost at any time after
  // creation for a variety of reasons (ie: browser resource management,
  // driver updates), so it's a good idea to always handle lost devices
  // gracefully.
  device.lost.then((info) => {
    device = null;
    if (info.reason !== 'destroyed')
      initializeWebGPU();
  });

  return true;
}

async function initDeviceData(matrix_width)
{
  // Matrices are like as follows:
  // [rows, columns, (values)]);
  matrixWidth = matrix_width;

  firstMatrix = new Float32Array((matrixWidth * matrixWidth) + 2);
  firstMatrix[0] = matrixWidth;
  firstMatrix[1] = matrixWidth;

  for (let i = 2; i < matrixWidth * matrixWidth + 2; i++)
    firstMatrix[i] = 1;

  gpuBufferFirstMatrix = device.createBuffer({
    mappedAtCreation: true,
    size: firstMatrix.byteLength,
    usage: GPUBufferUsage.STORAGE
  });
  arrayBufferFirstMatrix = gpuBufferFirstMatrix.getMappedRange();

  new Float32Array(arrayBufferFirstMatrix).set(firstMatrix);
  gpuBufferFirstMatrix.unmap();

  secondMatrix = new Float32Array((matrixWidth * matrixWidth) + 2);
  secondMatrix[0] = matrixWidth;
  secondMatrix[1] = matrixWidth;

  for (let i = 2; i < matrixWidth * matrixWidth + 2; i++)
    secondMatrix[i] = 1;

  gpuBufferSecondMatrix = device.createBuffer({
    mappedAtCreation: true,
    size: secondMatrix.byteLength,
    usage: GPUBufferUsage.STORAGE
  });
  arrayBufferSecondMatrix = gpuBufferSecondMatrix.getMappedRange();
  new Float32Array(arrayBufferSecondMatrix).set(secondMatrix);
  gpuBufferSecondMatrix.unmap();

  resultMatrixBufferSize = Float32Array.BYTES_PER_ELEMENT * (2 + firstMatrix[0] * secondMatrix[1]);
  resultMatrixBuffer = device.createBuffer({
    size: resultMatrixBufferSize,
    usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC
  });

  // Get a GPU buffer for reading in an unmapped state.
  gpuReadBuffer = device.createBuffer({
    size: resultMatrixBufferSize,
    usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ
  });
}

async function run()
{
  const shaderModule = device.createShaderModule({
    code: \`

      const BLOCK_SIZE_X = 16;
      const BLOCK_SIZE_Y = 16;

      struct Matrix {
        size : vec2<f32>,
        numbers: array<f32>,
      }

      @group(0) @binding(0) var<storage, read> firstMatrix : Matrix;
      @group(0) @binding(1) var<storage, read> secondMatrix : Matrix;
      @group(0) @binding(2) var<storage, read_write> resultMatrix : Matrix;

      @compute @workgroup_size(BLOCK_SIZE_X, BLOCK_SIZE_Y)
      fn main(@builtin(global_invocation_id) global_id : vec3<u32>) {
        // Guard against out-of-bounds work group sizes
        if (global_id.x >= u32(firstMatrix.size.x) || global_id.y >= u32(secondMatrix.size.y)) {
          return;
        }

        resultMatrix.size = vec2(firstMatrix.size.x, secondMatrix.size.y);

        let resultCell = vec2(global_id.x, global_id.y);
        var result = 0.0;
        for (var i = 0u; i < u32(firstMatrix.size.y); i = i + 1u) {
          let a = i + resultCell.x * u32(firstMatrix.size.y);
          let b = resultCell.y + i * u32(secondMatrix.size.y);
          result = result + firstMatrix.numbers[a] * secondMatrix.numbers[b];
        }

        let index = resultCell.y + resultCell.x * u32(secondMatrix.size.y);
        resultMatrix.numbers[index] = result;
      }
    \`
  });

  const computePipeline = device.createComputePipeline(
      { layout: 'auto', compute: { module: shaderModule, entryPoint: 'main' } });

  const bindGroup = device.createBindGroup({
    layout: computePipeline.getBindGroupLayout(0 /* index */),
    entries: [
      { binding: 0, resource: { buffer: gpuBufferFirstMatrix  } },
      { binding: 1, resource: { buffer: gpuBufferSecondMatrix } },
      { binding: 2, resource: { buffer: resultMatrixBuffer    } },
    ]
  });

  const commandEncoder = device.createCommandEncoder();

  const passEncoder = commandEncoder.beginComputePass();
  passEncoder.setPipeline(computePipeline);
  passEncoder.setBindGroup(0, bindGroup);
  const workgroupCountX = Math.ceil(firstMatrix[0] / BLOCK_SIZE_X);
  const workgroupCountY = Math.ceil(secondMatrix[1] / BLOCK_SIZE_Y);
  passEncoder.dispatchWorkgroups(workgroupCountX, workgroupCountY);
  passEncoder.end();

  commandEncoder.copyBufferToBuffer(
      resultMatrixBuffer /* source buffer */, 0 /* source offset */,
      gpuReadBuffer /* destination buffer */, 0 /* destination offset */,
      resultMatrixBufferSize /* size */
  );

  // Now finalizing the GPU commands, ready for submission.
  const gpuCommands = commandEncoder.finish();

  // From the CPU perspective, this is a good place to start measuring
  // GPU time (write before queue.submit call) Anything before here is
  // GPU work preparation. If we have device.queue.writeBuffer calls,
  // as they are synchronous, they will not be included in this measurement.
  const start = performance.now();

  device.queue.submit([gpuCommands]);

  await device.queue.onSubmittedWorkDone();

  // By now we are sure that the matmult operation is done. However, 
  // we cannot readily use the results. So, we can kind of differentiate 
  // between GPU compute time and GPU total execution time (ignoring
  // the Host-to-Device data transfer time)
  await gpuReadBuffer.mapAsync(GPUMapMode.READ);
  return performance.now() - start;
}

async function cleanup()
{
  // Freeing buffers
  gpuReadBuffer.unmap();

  gpuBufferFirstMatrix.destroy();
  gpuBufferSecondMatrix.destroy();
  resultMatrixBuffer.destroy();
}

const benchmark = {
  initializeWebGPU: initializeWebGPU,
  initDeviceData,
  run,
  cleanup
};

async function runBenchmark()
{
  try
  {
    await benchmark.initializeWebGPU();
    await benchmark.initDeviceData(10);

    // Warm up gpu - get more accurate results in the real run
    await benchmark.run();
    benchmark.cleanup();

    await benchmark.initDeviceData(2000);
    let result = await benchmark.run();
    benchmark.cleanup();

    return result;
  }
  catch (e)
  {
    return 'Unable to run benchmark';
  }
}

runBenchmark();
`
